/**
 * A hook that returns whether the article (either passed in or from context) is active.
 *
 * An article is active when it is the article in the url param for the current page.
 *
 * @param accepts an optional `articleId` as the parameter.
 *
 * - If no `article` is found from `useArticle`, returns `false`.
 * - If an `articleId` is provided, it checks to see if it matches with the `article` currently in `useArticle` and returns `article`.
 * - If a `fragment` is found from `useArticleFragment`, it checks if the fragment.id matches articleId and returns `fragment`.
 * - Returns `false` if no `article`, `articleId` or `fragment` are found.
 * @module
 */
import { useArticleFragment } from './useArticleFragment';
import { useArticleFull } from './useArticleFull';
/**
 *
 * # useIsArticleActive
 *
 * Usage example:
 *
 * ```javascript
 * import { useIsArticleActive } from '@elevio/kb-kit/lib/hooks';
 *
 * const ActiveArticleChecker = ({ articleId }) => {
 *   const isActive = useIsArticleActive({ articleId });
 *   console.log(isActive); // boolean
 *
 *   return <div>{`Article is ${isActive ? 'active ✅' : 'inactive ❌'}`}</div>;
 * };
 * ```
 */
export function useIsArticleActive({ articleId, }) {
    const article = useArticleFull();
    const fragment = useArticleFragment();
    if (!article)
        return false;
    if (articleId)
        return articleId === article.id;
    if (fragment)
        return fragment.id === article.id;
    return false;
}
//# sourceMappingURL=useIsArticleActive.js.map